import { Box } from "@mui/material";
import type { ReactNode } from "react";
import { macro } from "./macroTokens";
import { v2, nums } from "./v2Tokens";
import { GRAM } from "./format";
import { energyFigure, macroFigure } from "./nutritionUnits";
import type { PointsMacroKey } from "./nutritionUnits";
import { useNutritionDisplayMode } from "../../../../contexts/NutritionDisplayContext";
import type { MacroSet, MacroTotals } from "../model/nutritionV2Model";

const Sep = () => (
  <Box component="span" sx={{ color: v2.ink4, fontSize: "10px" }}>
    ·
  </Box>
);

const round1 = (n: number): number => Math.round((Number(n) || 0) * 10) / 10;

/**
 * A macro as `48` in its own colour followed by `גר׳ חלבון` in plain text. Only the
 * NUMBER is coloured — colouring the words too made a row of five macros read as five
 * competing labels instead of five values.
 *
 * `macroKey` puts the figure in points when the company counts in points (`1.9 נק׳ חלבון`).
 * Fibre has no key: it has no point value and stays in grams in both modes.
 */
const Macro = ({
  value,
  color,
  label,
  macroKey,
}: {
  value: number;
  color: string;
  label: string;
  macroKey?: PointsMacroKey;
}) => {
  const mode = useNutritionDisplayMode();
  const f = macroKey ? macroFigure(macroKey, value, mode) : { text: String(round1(value)), unit: GRAM };
  return (
    <Box component="span">
      <Box component="span" sx={{ color, fontWeight: 700 }}>
        {f.text}
      </Box>
      <Box component="span" sx={{ color: v2.ink2, fontWeight: 500 }}>
        {` ${f.unit} ${label}`}
      </Box>
    </Box>
  );
};

/** Calories keep the same shape: the number stands out, the unit does not. */
const Calories = ({ value, size }: { value: number; size?: number }) => {
  const f = energyFigure(value, useNutritionDisplayMode(), { short: true });
  return (
    <Box component="span" sx={size ? { fontSize: `${size}px` } : undefined}>
      <Box component="span" sx={{ color: v2.ink, fontWeight: 700 }}>
        {f.text}
      </Box>
      <Box component="span" sx={{ color: v2.ink2, fontWeight: 500 }}>
        {` ${f.unit}`}
      </Box>
    </Box>
  );
};

/** Quality protein IS protein, so it converts like protein — and its number shares the protein hue. */
const QualityProtein = ({ value }: { value: number }) => {
  const f = macroFigure("protein", value, useNutritionDisplayMode());
  return (
    <Box component="span" sx={{ color: v2.ink2, fontWeight: 500 }}>
      (
      <Box component="span" sx={{ color: macro.protein.text, fontWeight: 700 }}>
        {f.text}
      </Box>
      {` ${f.unit} איכותי)`}
    </Box>
  );
};

/**
 * The type scale of a macro line and of the total row that sits under it — exported so the
 * RELATIONSHIP between them can be tested.
 *
 * It has to be tested through these objects rather than through a rendered element: both sizes are
 * responsive now, and a responsive `sx` value is unreadable in jsdom (MUI wraps even the `xs` value
 * in `@media (min-width:0px)`, which jsdom does not evaluate, so `toHaveStyle` sees nothing). The
 * thing that must never break is that the total out-weighs the lines it totals — at BOTH widths.
 */
export const macroLineFont = (size: number) => ({
  // half a step down on a phone: five macros with their units is a long line, and the difference
  // between 11 and 11.5px buys most of a wrapped line back
  xs: `${size - 0.5}px`,
  md: `${size}px`,
});

/** The default `size` of a MacroLine — what a food row's macros are set at. */
export const MACRO_LINE_DEFAULT_SIZE = 11.5;

export const TOTAL_ROW = {
  // still a step above the food rows it totals — see the note on TotalRow — just a smaller step on
  // a phone, where the same line has a third of the width to say it in
  font: { xs: "13px", md: "15px" },
  // height from padding rather than a min-height, so an empty-ish meal and a five-food one frame
  // the text identically
  padding: { xs: "12px 10px", md: "15px 14px" },
};

/**
 * Every macro is ALWAYS shown with its label — a bare "48" tells the trainer nothing
 * about which macro it is. Used in meal headers, alternative headers and food rows.
 */
export const MacroLine = ({
  totals,
  size = MACRO_LINE_DEFAULT_SIZE,
  caloriesSize,
  plain,
}: {
  totals: MacroSet;
  size?: number;
  /** calories a notch larger than the macros beside them — used in the food rows */
  caloriesSize?: number;
  /**
   * Drops the macro hues, numbers included. A food row already sits under cards that
   * carry a colour per macro; repeating it on every row turned the list into stripes.
   */
  plain?: boolean;
}) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      // a row of five macros wraps to two or three lines on a phone, so THERE the lines sit closer
      // together than the gap between macros — at one gap the wrapped line reads as a second,
      // unrelated row of figures. Above md the single `gap: 5px` it always had.
      columnGap: "5px",
      rowGap: { xs: "2px", md: "5px" },
      lineHeight: { xs: 1.45, md: "inherit" },
      flexWrap: "wrap",
      fontSize: macroLineFont(size),
      ...nums,
    }}
  >
    <Calories value={totals.calories} size={caloriesSize} />
    <Sep />
    <Macro value={totals.protein} color={plain ? v2.ink : macro.protein.text} label="חלבון" macroKey="protein" />
    <Sep />
    <Macro value={totals.carbs} color={plain ? v2.ink : macro.carbs.text} label="פחמימות" macroKey="carbs" />
    <Sep />
    <Macro value={totals.fats} color={plain ? v2.ink : macro.fats.text} label="שומן" macroKey="fats" />
    <Sep />
    <Macro value={totals.fiber} color={plain ? v2.ink : macro.fiber.text} label="סיבים" />
  </Box>
);

/**
 * Meal / list total row, including the quality-protein call-out and fiber.
 *
 * <p>Set a step larger than the food rows above it on purpose. This is the line a trainer reads
 * while they build — the running answer to "what does this meal come to" — and at the food rows'
 * own size it disappeared into them, which is what a total must never do. The height comes from
 * padding rather than from a min-height so an empty-ish meal and a five-food one still frame the
 * text identically.
 *
 * <p>Used by both the meal card ("סה״כ ארוחה") and an alternative meal ("סה״כ חלופה"). Both are
 * totals, so both should read as one.
 */
export const TotalRow = ({
  totals,
  label = "סה״כ ארוחה:",
  action,
}: {
  totals: MacroTotals;
  label?: string;
  /**
   * The row's far end. The meal card puts its alternative-meals control here: that control had a
   * band of its own carrying a count and one button, which is a whole row's height for two words.
   */
  action?: ReactNode;
}) => (
  <Box
    data-testid="total-row"
    sx={{
      display: "flex",
      alignItems: "center",
      columnGap: "7px",
      rowGap: { xs: "3px", md: "7px" },
      flexWrap: "wrap",
      padding: TOTAL_ROW.padding,
      borderTop: `1px solid ${v2.lineSoft}`,
      background: v2.canvas,
      fontSize: TOTAL_ROW.font,
      ...nums,
    }}
  >
    <Box component="span" sx={{ fontWeight: 700, color: v2.ink }}>
      {label}
    </Box>
    <Calories value={totals.calories} size={17.5} />
    <Sep />
    <Macro value={totals.protein} color={macro.protein.text} label="חלבון" macroKey="protein" />
    {totals.qualityProtein > 0 && <QualityProtein value={totals.qualityProtein} />}
    <Sep />
    <Macro value={totals.carbs} color={macro.carbs.text} label="פחמימות" macroKey="carbs" />
    <Sep />
    <Macro value={totals.fats} color={macro.fats.text} label="שומן" macroKey="fats" />
    <Sep />
    <Macro value={totals.fiber} color={macro.fiber.text} label="סיבים" />
    {/* On a phone the macros already fill the row, so an auto margin has nothing to push against
        and the control ends up wedged against the last figure. It takes a line of its own instead. */}
    {action && (
      <Box sx={{ marginInlineStart: { xs: 0, md: "auto" }, width: { xs: "100%", md: "auto" } }}>
        {action}
      </Box>
    )}
  </Box>
);
